import type { Locale } from '@/lib/i18n'

export const round2 = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100

/** Deterministic number formatting, identical on the server and in the browser. */
export const formatAmount = (value: number, fractionDigits: 'auto' | 0 | 2 = 'auto'): string => {
    const safe = Number.isFinite(value) ? value : 0
    const rounded = round2(safe)
    const [integer, fraction] = Math.abs(rounded).toFixed(2).split('.')
    const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    const sign = rounded < 0 ? '-' : ''

    if (fractionDigits === 0) return sign + Math.round(Math.abs(rounded)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    if (fractionDigits === 2 || fraction !== '00') return `${sign}${grouped}.${fraction}`

    return sign + grouped
}

export const formatInteger = (value: number): string => formatAmount(Math.round(value), 0)

export const currencyLabel = (locale: Locale): string => locale === 'en' ? 'EGP' : 'ج.م'

/** Plain-text price, for emails, WhatsApp messages and the assistant. */
export const formatPrice = (value: number, locale: Locale): string =>
    locale === 'en' ? `EGP ${formatAmount(value)}` : `${formatAmount(value)} ج.م`

export const percentOff = (original: number, final: number): number => {
    if (!original || final >= original) return 0

    return Math.round(((original - final) / original) * 100)
}

const dateLocale = (locale: Locale): string => locale === 'en' ? 'en-GB' : 'ar-EG-u-nu-latn'

export const formatDate = (iso: string | null | undefined, locale: Locale, options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }): string => {
    if (!iso) return ''

    const date = new Date(iso)

    if (Number.isNaN(date.getTime())) return ''

    return new Intl.DateTimeFormat(dateLocale(locale), { timeZone: 'Africa/Cairo', ...options }).format(date)
}

export const formatDateTime = (iso: string | null | undefined, locale: Locale): string =>
    formatDate(iso, locale, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })

/** `YYYY-MM-DD` of an instant in Cairo time, used to group sales by day. */
export const cairoDayKey = (iso: string): string => {
    const date = new Date(iso)
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Cairo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date)

    return parts
}

/** Converts an ISO instant to the value a `datetime-local` input expects, in the browser's own time zone. */
export const toDateTimeLocal = (iso: string | null | undefined): string => {
    if (!iso) return ''

    const date = new Date(iso)

    if (Number.isNaN(date.getTime())) return ''

    const offset = date.getTimezoneOffset()
    const local = new Date(date.getTime() - offset * 60000)

    return local.toISOString().slice(0, 16)
}

/** Converts a `datetime-local` input value (browser time zone) to an ISO instant. */
export const fromDateTimeLocal = (value: string): string | null => {
    if (!value) return null

    const date = new Date(value)

    return Number.isNaN(date.getTime()) ? null : date.toISOString()
}
